import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { authService } from '../services/auth.service'
import type { ChangePasswordDto, ForgotPasswordDto, LoginDto, ResetPasswordDto } from '../types'
import { ROUTES } from '@config/routes'
import { QUERY_KEYS } from '@shared/constants'
import type { NestjsPaginateParams } from '@shared/components/ui/data-table'

export function useCurrentUser() {
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS.AUTH.ME,
    queryFn: authService.getMe,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
  return { user: data ?? null, isLoading }
}

export function useLogin() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const login = async (dto: LoginDto) => {
    await authService.login(dto)
    await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.ME })
    navigate(ROUTES.DASHBOARD)
  }

  return { login }
}

export function useLogout() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const logout = async () => {
    await authService.logout()
    queryClient.clear()
    navigate(ROUTES.LOGIN)
  }

  return { logout }
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (dto: ChangePasswordDto) => authService.changePassword(dto),
    onSuccess: () => toast.success('Đổi mật khẩu thành công'),
    onError: () => toast.error('Mật khẩu hiện tại không đúng'),
  })
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (dto: ForgotPasswordDto) => authService.forgotPassword(dto),
  })
}

export function useResetPassword() {
  const navigate = useNavigate()
  return useMutation({
    mutationFn: (dto: ResetPasswordDto) => authService.resetPassword(dto),
    onSuccess: () => {
      toast.success('Đặt lại mật khẩu thành công, vui lòng đăng nhập lại')
      navigate(ROUTES.LOGIN)
    },
    onError: () => toast.error('Token không hợp lệ hoặc đã hết hạn'),
  })
}

export function useAdminSessions(params: NestjsPaginateParams) {
  return useQuery({
    queryKey: [...QUERY_KEYS.AUTH.SESSIONS_ADMIN, JSON.stringify(params)],
    queryFn: () => authService.listAdminSessions(params),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

export function useRevokeAdminSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => authService.revokeAdminSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.SESSIONS_ADMIN })
      toast.success('Session đã bị thu hồi')
    },
    onError: () => toast.error('Không thể thu hồi session'),
  })
}
